import { FeatureFlag, featureIsEnabled } from '@assetaccountant/core/services/feature-flag';

// This is a generalized environment that must have all config values
// populated from the build pipeline. It's currently used to setup a
// test environment in Azure Container Apps, but is not specific for
// that purpose. This could become the default environment.ts in the
// future if we ensure every pipeline supplies all configs values.

const apiUrl = import.meta.env?.NG_APP_API_URL;
const apiStagingUrl = import.meta.env?.NG_APP_API_STAGING_URL;
const identityUrl = import.meta.env?.NG_APP_IDENTITY_URL;
const uiUrl = window.location.origin;
const uiStagingUrl = import.meta.env?.NG_APP_UI_STAGING_URL;
const lqmsWebAppUrl = import.meta.env?.NG_APP_LQMS_WEB_APP_URL ?? '';

export const environment = {
  name: 'Cloud',
  lqmsWebApp: lqmsWebAppUrl,
  lqmsLoginPath: '/Login/Identity?status=1',
  navigationApiUrl: import.meta.env?.NG_APP_NAVIGATION_API_URL ?? '',
  production: true,
  appInsights: {
    apiKey: import.meta.env?.NG_APP_INSIGHTS_KEY,
  },
  get servicesApiUrl() {
    return featureIsEnabled(FeatureFlag.UseStagingApi)
      ? this.servicesApiUrlStagingSlot
      : this.servicesApiUrlProdSlot;
  },
  servicesApiUrlProdSlot: apiUrl,
  servicesApiUrlStagingSlot: apiStagingUrl,
  // DEPRECATED-PENDING-DELETION: rate lookups go through servicesApiUrl (/api/rates/search) now.
  ratesApiUrl: import.meta.env?.NG_APP_RATES_API_URL,
  // DEPRECATED-PENDING-DELETION: no longer sent to the rates server (see DepreciationService).
  ratesApiKey: import.meta.env?.NG_APP_RATES_API_KEY,
  stripeApiKey: import.meta.env?.NG_APP_STRIPE_API_KEY,
  quickBooks: {
    scope: 'openid profile email phone address com.intuit.quickbooks.accounting',
    clientId: import.meta.env?.NG_APP_QUICKBOOKS_CLIENT_ID,
    issuer: 'https://appcenter.intuit.com/connect/oauth2',
    redirectUri: `${uiUrl}/signin-oidc`,
  },
  xero: {
    scope: 'openid profile email offline_access assets accounting.transactions accounting.settings',
    clientId: import.meta.env?.NG_APP_XERO_CLIENT_ID,
    issuer: 'https://login.xero.com/identity/connect/authorize',
    redirectUri: `${uiUrl}/xero-signin-oidc`,
  },
  integrations: {
    dynamics: {
      scope:
        'openid profile email offline_access https://api.businesscentral.dynamics.com/Financials.ReadWrite.All',
      clientId: import.meta.env?.NG_APP_DYNAMICS_CLIENT_ID,
      issuer: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
      redirectUri: `${uiUrl}/dynamics-signin-oidc`,
    },
    sageV2: {
      scope: 'openid profile email offline_access',
      clientId: import.meta.env?.NG_APP_SAGE_V2_CLIENT_ID,
      issuer: 'https://api.intacct.com/ia/api/v1/oauth2/authorize',
      redirectUri: `${uiUrl}/sage-signin-oidc`,
    },
  },
  get isStagingUi() {
    return false;
  },
  get openIdConnect() {
    return this.isStagingUi ? this.stagingOpenIdConnect : this.prodOpenIdConnect;
  },
  prodOpenIdConnect: {
    issuer: identityUrl,
    redirectUri: uiUrl,
    silentRefreshRedirectUri: `${uiUrl}/silent-refresh.html`,
    clientId: 'AssetAccountant',
    requireHttps: true,
    showDebugInformation: false,
    scope: 'openid profile email asset-accountant-api.registers',
    strictDiscoveryDocumentValidation: true,
  },
  stagingOpenIdConnect: {
    issuer: identityUrl,
    redirectUri: uiStagingUrl,
    silentRefreshRedirectUri: `${uiStagingUrl}/silent-refresh.html`,
    clientId: 'AssetAccountantStaging',
    requireHttps: true,
    showDebugInformation: false,
    scope: 'openid profile email asset-accountant-api.registers',
    strictDiscoveryDocumentValidation: true,
  },
  versionCheckInterval: 30 * 60 * 1000, // 30 mins
  providers: [],
  warn: (message: string) => console.error(message),
  defaultFeatures: ['Minor fixes and enhancements.'],
  salesEmail: '',
  supportEmail: '',
  webinarEmail: '',
  knowledgeBaseUrl: 'https://leasequery.zendesk.com/agent/home/tickets',
  amplitude: {
    apiKey: import.meta.env?.NG_APP_AMPLITUDE_API_KEY,
  },
  // Datadog RUM, Heap and Appcues. Each tool is on only when its *_ENABLED flag is 'true'
  // and its ids are set; parsed by core/analytics/analytics-config.ts.
  analytics: {
    datadog: {
      enabled: import.meta.env?.NG_APP_DATADOG_ENABLED,
      env: import.meta.env?.NG_APP_DATADOG_ENV,
      applicationId: import.meta.env?.NG_APP_DATADOG_APPLICATION_ID,
      clientToken: import.meta.env?.NG_APP_DATADOG_CLIENT_TOKEN,
      sessionReplaySampleRate: import.meta.env?.NG_APP_DATADOG_SESSION_REPLAY_SAMPLE_RATE,
    },
    heap: {
      enabled: import.meta.env?.NG_APP_HEAP_ENABLED,
      appId: import.meta.env?.NG_APP_HEAP_APP_ID,
    },
    appcues: {
      enabled: import.meta.env?.NG_APP_APPCUES_ENABLED,
      accountId: import.meta.env?.NG_APP_APPCUES_ACCOUNT_ID,
    },
  },
};
